import { useMemo } from 'react';
import {
  BODY_PAINT_STATE,
  ITEM_REPAIR,
  ITEM_REPAIR_NI,
} from '@/consts/bodyAndPaint';
import { BodyAndPaintRepairRequestParams } from '@/types/api/bodyAndPaint';
import {
  RepairItemSelectInfoType,
  RepairRequestIDInfoType,
  RepairRequestItemType,
} from '@/types/bodyAndPaint';

interface RepairRequestDataHookProps {
  data?: {
    ids: RepairRequestIDInfoType;
    repairSelectList: RepairItemSelectInfoType;
    repairRequestItem: {
      status: number;
      carName: string;
      category: string;
      factoryName: string;
      totalAmount: number;
    };
  };
}
export const useRepairRequestData = ({ data }: RepairRequestDataHookProps) => {
  const { ids, repairSelectList, repairRequestItem } = useMemo(() => {
    return {
      ids: data?.ids ?? ({} as RepairRequestIDInfoType),
      repairSelectList: data?.repairSelectList,
      repairRequestItem: {
        status: data?.repairRequestItem?.status ?? -1,
        carName: data?.repairRequestItem?.carName ?? '',
        category: data?.repairRequestItem?.category ?? '',
        factoryName: data?.repairRequestItem?.factoryName ?? '',
        totalAmount: data?.repairRequestItem?.totalAmount ?? 0,
      },
    };
  }, [data]);

  const isEstimating: boolean = useMemo(() => {
    return (
      repairRequestItem.status !== BODY_PAINT_STATE.ESTIMATE_APPLY &&
      repairRequestItem.status !== BODY_PAINT_STATE.APPLIES_RESULT
    );
  }, [repairRequestItem.status]);

  const repairRequestData = useMemo(() => {
    const RequestInfoLabel: Record<string, string> = {
      対象車両: repairRequestItem?.carName ?? '',
      入庫区分: repairRequestItem?.category ?? '',
      入庫工場: repairRequestItem?.factoryName ?? '',
      金額: (repairRequestItem?.totalAmount ?? 0).toLocaleString('ja-JP'),
    };
    const requestInfoList = Object.entries(RequestInfoLabel).map(
      ([key, value]) => {
        return { label: key, item: value } as RepairRequestItemType;
      },
    );

    const requestItemInfoList = (repairSelectList ?? []).reduce(
      (list, infoItem) => {
        if (
          infoItem.repair_select !== undefined &&
          infoItem.repair_select >= 0
        ) {
          if (repairRequestItem.status != BODY_PAINT_STATE.ESTIMATE_APPLY) {
            const key = Object.keys(ITEM_REPAIR)[
              infoItem.repair_select
            ] as keyof typeof ITEM_REPAIR;
            if (ITEM_REPAIR[key] === ITEM_REPAIR.USE_INSURANCE) {
              list.push({ label: '整備項目', item: infoItem.repair_item_name });
              list.push({
                label: '保険利用の有無',
                item: '利用する',
              });
            } else if (ITEM_REPAIR[key] === ITEM_REPAIR.NO_INSURANCE) {
              list.push({ label: '整備項目', item: infoItem.repair_item_name });
              list.push({
                label: '保険利用の有無',
                item: '利用しない',
              });
            }
          } else {
            const key = Object.keys(ITEM_REPAIR_NI)[
              infoItem.repair_select
            ] as keyof typeof ITEM_REPAIR_NI;
            list.push({ label: '整備項目', item: infoItem.repair_item_name });
            list.push({
              label: '保険利用の有無',
              item: 'しない',
            });
          }
        }
        return list;
      },
      requestInfoList,
    );
    return requestItemInfoList;
  }, [repairRequestItem, repairSelectList]);

  const repairRequestParam: BodyAndPaintRepairRequestParams = useMemo(() => {
    const repairItemList = (repairSelectList ?? []).reduce(
      (list, item) => {
        if (item.repair_select !== undefined && item.repair_select >= 0) {
          if (repairRequestItem.status != BODY_PAINT_STATE.ESTIMATE_APPLY) {
            const key = Object.keys(ITEM_REPAIR)[
              item.repair_select
            ] as keyof typeof ITEM_REPAIR;
            const repairItem = {
              t_pm_car_inspection_need_maintenance_id: item.id,
              repair_contents: ITEM_REPAIR[key],
            };
            list.push(repairItem);
          } else {
            const key = Object.keys(ITEM_REPAIR_NI)[
              item.repair_select
            ] as keyof typeof ITEM_REPAIR_NI;
            const repairItem = {
              t_pm_car_inspection_need_maintenance_id: item.id,
              repair_contents: ITEM_REPAIR_NI[key],
            };
            list.push(repairItem);
          }
        }
        return list;
      },
      [] as {
        t_pm_car_inspection_need_maintenance_id: number;
        repair_contents: number;
      }[],
    );
    const parameters: BodyAndPaintRepairRequestParams = {
      m_customer_id: ids?.customerID ?? 0,
      t_estimate_id: ids?.estimateID ?? 0,
      m_affiliate_id: ids?.affiliateID ?? 0,
      t_body_paint_id: ids?.bodyPaintID ?? 0,
      repair_item: repairItemList,
    };
    return parameters;
  }, [ids, repairSelectList]);

  return { isEstimating, repairRequestData, repairRequestParam };
};
